import { FileText, Loader, AlertCircle, Clock, CheckCircle, XCircle, FileCode, Trash2, Globe } from "lucide-react";
import { format } from "date-fns";
import { ru, enUS } from "date-fns/locale";
import { Link } from "react-router-dom";
import { useState } from "react";
import { formatDuration } from "@/lib/utils";
import { RecordWithTemplate } from "@/hooks/useRecords";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { TagChip } from "@/components/records/TagChip";
import { useTranslation, Trans } from "react-i18next";
import { DeleteRecordDialog } from "@/components/records/DeleteRecordDialog";
import { useDeleteRecord } from "@/hooks/useDeleteRecord";

interface RecordCardProps {
  record: RecordWithTemplate;
  disableNavigation?: boolean;
  isPublished?: boolean;
  shareUrl?: string;
}

const StatusBadge = ({ status, compact = false }: { status: string; compact?: boolean }) => {
  const { t } = useTranslation();

  const statusConfig = {
    completed: {
      icon: <CheckCircle size={compact ? 14 : 18} className="text-emerald-600" />,
      text: t('records.status.completed'),
      className: "text-emerald-800 bg-emerald-50 border-emerald-200",
    },
    processing: {
      icon: <Loader size={compact ? 14 : 18} className="text-blue-600 animate-spin" />,
      text: t('records.status.processing'),
      className: "text-blue-800 bg-blue-50 border-blue-200",
    },
    waiting: {
      icon: <Clock size={compact ? 14 : 18} className="text-amber-600" />,
      text: t('records.status.waiting'),
      className: "text-amber-800 bg-amber-50 border-amber-200",
    },
    queued: {
      icon: <Clock size={compact ? 14 : 18} className="text-yellow-600" />,
      text: t('records.status.queued'),
      className: "text-yellow-800 bg-yellow-50 border-yellow-200",
    },
    failed: {
      icon: <XCircle size={compact ? 14 : 18} className="text-red-600" />,
      text: t('records.status.failed'),
      className: "text-red-800 bg-red-50 border-red-200",
    },
    cancelled: {
      icon: <AlertCircle size={compact ? 14 : 18} className="text-slate-500" />,
      text: t('records.status.cancelled'),
      className: "text-slate-700 bg-slate-50 border-slate-200",
    },
  };

  // @ts-expect-error dynamic status key access
  const config = statusConfig[status] || {
    icon: <AlertCircle size={compact ? 14 : 18} className="text-muted-foreground" />,
    text: t('records.status.unknown'),
    className: "text-foreground bg-muted",
  };

  const padding = compact ? "px-2 py-1" : "px-3 py-1.5";
  const textSize = compact ? "text-[10px]" : "text-xs";

  return (
    <div className={`flex items-center gap-1.5 ${padding} rounded-full ${config.className} border shadow-sm`}>
      {config.icon}
      <span className={`${textSize} font-semibold`}>{config.text}</span>
    </div>
  );
};

const RecordInfo = ({ record, isPublished, shareUrl }: { record: RecordWithTemplate; isPublished?: boolean; shareUrl?: string }) => {
  const { t } = useTranslation();
  return (
    <div className="flex-1 min-w-0">
      <h3 className="font-medium text-base text-foreground flex items-center gap-2 min-w-0">
        {isPublished && <Globe size={16} className="text-brand-600 flex-shrink-0" />}
        <span className="truncate">{record.name}</span>
      </h3>
      <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
        {record.template ? (
          <span className="flex items-center gap-1.5">
            <FileCode size={14} className="text-brand-400" />
            <span className="truncate">{record.template.name}</span>
          </span>
        ) : (
          <span className="flex items-center gap-1.5">
            <FileText size={14} className="text-muted-foreground" />
            <span>{t('records.no_template')}</span>
          </span>
        )}
      </div>

      {/* Теги */}
      {record.tags && record.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {record.tags.slice(0, 3).map((tag) => (
            <TagChip
              key={tag.id}
              tag={tag}
              size="sm"
              showContextMenu={false}
            />
          ))}
          {record.tags.length > 3 && (
            <span className="px-2 py-1 text-xs text-muted-foreground bg-muted rounded-full border">
              +{record.tags.length - 3}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const StatusInfo = ({ record, showStatus = true }: { record: RecordWithTemplate; showStatus?: boolean }) => {
  const { i18n } = useTranslation();

  const formatDate = (dateString: string) => {
    try {
      const locale = i18n.language === 'en' ? enUS : ru;
      return format(new Date(dateString), "dd MMMM yyyy, HH:mm", { locale });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <div className="flex flex-col items-end gap-2 flex-shrink-0">
      {showStatus && <StatusBadge status={record.processingStatus} />}
      <div className="text-xs text-muted-foreground">{formatDate(record.createdAt)}</div>
    </div>
  );
};

export const RecordCard = ({ record, disableNavigation = false, isPublished = false, shareUrl }: RecordCardProps) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const deleteRecord = useDeleteRecord();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();

  const handleCardClick = (e: React.MouseEvent) => {
    if (disableNavigation) return;

    // Если статус не completed, показываем предупреждение и не даем открыть
    if (record.processingStatus !== "completed") {
      e.preventDefault();
      e.stopPropagation();

      if (
        record.processingStatus === "processing" ||
        record.processingStatus === "waiting" ||
        record.processingStatus === "queued"
      ) {
        toast({
          title: t('records.processing_toast_title'),
          description: t('records.processing_toast_desc'),
          duration: 3000,
        });
      }
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    deleteRecord.mutate(record.id);
    setShowDeleteDialog(false);
  };

  // Получаем цвет текста в соответствии со статусом
  const getStatusTextColor = (status: string) => {
    const statusColors: Record<string, string> = {
      completed: "text-green-600",
      processing: "text-blue-600",
      waiting: "text-orange-600",
      queued: "text-yellow-600",
      failed: "text-red-600",
      cancelled: "text-muted-foreground",
    };

    return statusColors[status] || "text-muted-foreground";
  };

  const showStatus = !disableNavigation;

  return (
    <>
      <Card className="overflow-hidden hover:shadow-lg hover:scale-[1.02] transition-all duration-300 group cursor-pointer border border-border/50 hover:border-brand-200">
        <div className="relative">
          {/* Кнопка удаления в левом верхнем углу для записей с ошибкой */}
          {record.processingStatus === "failed" && (
            <button
              onClick={handleDeleteClick}
              className="absolute top-3 left-3 z-20 p-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700 transition-colors opacity-70 hover:opacity-100 shadow-sm"
              aria-label={t('records.delete_record_label')}
            >
              <Trash2 size={16} />
            </button>
          )}

          <div className="p-4 sm:p-5">
            {/* Десктопная версия */}
            <div className="hidden sm:flex items-start justify-between gap-4">
              <div className="flex items-start gap-4 flex-1 min-w-0">
                <div className="flex-shrink-0 h-16 w-12 bg-gradient-to-br from-brand-50 to-brand-100 rounded-xl flex flex-col items-center justify-center border border-brand-200 shadow-sm py-1 group-hover:shadow-md transition-shadow">
                  <FileText size={18} className="text-brand-600 group-hover:text-brand-700 transition-colors" />
                  <span className="text-[9px] font-bold text-brand-700 leading-none mt-1">
                    {formatDuration(record.duration)}
                  </span>
                </div>
                <RecordInfo record={record} isPublished={isPublished} shareUrl={shareUrl} />
              </div>
              <StatusInfo record={record} showStatus={showStatus} />
            </div>

            {/* Мобильная версия */}
            <div className="sm:hidden">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {/* Контур с иконкой и продолжительностью */}
                  <div className="flex-shrink-0">
                    <div className="h-14 w-11 bg-gradient-to-br from-brand-50 to-brand-100 rounded-xl flex flex-col items-center justify-center border border-brand-200 shadow-sm py-1 group-hover:shadow-md transition-shadow">
                      <FileText size={16} className="text-brand-600 group-hover:text-brand-700 transition-colors" />
                      <span className="text-[8px] font-bold text-brand-700 leading-none mt-0.5">
                        {formatDuration(record.duration)}
                      </span>
                    </div>
                  </div>

                  <div className="flex-1 min-w-0 pt-0.5">
                    <h3 className="font-semibold text-[15px] leading-tight text-foreground flex items-center gap-2 min-w-0">
                      {isPublished && <Globe size={14} className="text-brand-600 flex-shrink-0" />}
                      <span className="truncate">{record.name}</span>
                    </h3>
                    <div className="flex items-center text-xs text-muted-foreground mt-1">
                      {record.template ? (
                        <span className="flex items-center gap-1.5">
                          <FileCode size={12} className="text-brand-400" />
                          <span className="truncate">{record.template.name}</span>
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5">
                          <FileText size={12} className="text-muted-foreground" />
                          <span>{t('records.no_template')}</span>
                        </span>
                      )}
                    </div>

                    {/* Теги для мобильной версии */}
                    {record.tags && record.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {record.tags.slice(0, 2).map((tag) => (
                          <TagChip
                            key={tag.id}
                            tag={tag}
                            size="sm"
                            showContextMenu={false}
                          />
                        ))}
                        {record.tags.length > 2 && (
                          <span className="px-1.5 py-0.5 text-[10px] text-muted-foreground bg-muted rounded-full border">
                            +{record.tags.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Статус и дата справа */}
                <div className="flex-shrink-0 pt-0.5 flex flex-col items-end gap-2">
                  {showStatus && <StatusBadge status={record.processingStatus} compact />}
                  <div className="text-[11px] text-muted-foreground font-medium">
                    {format(new Date(record.createdAt), "dd MMM, HH:mm", { locale: i18n.language === 'en' ? enUS : ru })}
                  </div>
                </div>
              </div>
            </div>

            {record.processing_status_desc && (
              <div className={`mt-3 text-sm ${getStatusTextColor(record.processingStatus)}`}>
                {record.processing_status_desc}
              </div>
            )}
          </div>

          {!disableNavigation && record.processingStatus === "completed" && (
            <Link
              to={`/record/${record.id}`}
              className="absolute inset-0 z-10 cursor-pointer"
              aria-label={t('records.open_record_label', { name: record.name })}
            />
          )}

          {!disableNavigation && record.processingStatus !== "completed" && record.processingStatus !== "failed" && (
            <div
              className="absolute inset-0 z-10 cursor-pointer"
              onClick={handleCardClick}
              aria-label={t('records.record_processing_label', { name: record.name })}
            />
          )}
        </div>
      </Card>

      <DeleteRecordDialog
        open={showDeleteDialog}
        recordName={record.name}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        isDeleting={deleteRecord.isPending}
      />
    </>
  );
};

export default RecordCard;
